import React, { useRef, useState } from 'react'
import { FiChevronDown } from 'react-icons/fi'
import { defaultMemoize } from 'reselect'
import Flex from 'typescript-styled-flex'
import { Fade, Paper, Popper } from '@material-ui/core'
import Flag from '../../../../../components/Template/Flag'
import { MenuLinkItem } from '../../../../../components/Template/Header'
import { CountryCode, countryCodes } from '../../../../../i18n/countryCodes'
import { IWithSize } from '../../../../BookingCalendar/Calendar/Calendar'

const calculateMargin = defaultMemoize((index: number, isLast: boolean) => {
  if (isLast) {
    return 0
  }
  if (index === 0 || index % 2 === 0) {
    return 10
  }

  return 0
})

function LanguageSwitcher({
  selectedLanguage,
  switchLanguage,
  className,
  size = 1,
}: ILanguageSwitcherProps &
  ILanguageSwitcherDispatch &
  IClassName &
  IWithSize) {
  const refMenu = useRef(null)
  const [isOpen, setIsOpen] = useState(false)

  let showTimeout

  let canClose = true

  function setShouldClose() {
    if (isOpen) {
      canClose = false
    }
  }

  function togglePopper() {
    if (!canClose) {
      canClose = true
    } else {
      showTimeout = setTimeout(() => setIsOpen(true), 75)
    }
  }

  function closePopper() {
    setTimeout(() => {
      if (!canClose) {
        clearTimeout(showTimeout)
        setIsOpen(false)
      }
    }, 1)
  }

  function shouldClose() {
    setTimeout(() => {
      if (canClose) {
        clearTimeout(showTimeout)
        setIsOpen(false)
      }
    }, 1)
  }

  function changeLanguage(lang: CountryCode) {
    return function () {
      setIsOpen(false)
      switchLanguage(lang)()
    }
  }

  return (
    <React.Fragment>
      <MenuLinkItem
        size={size}
        ref={refMenu}
        onMouseEnter={togglePopper}
        onMouseLeave={shouldClose}
        center={true}
        className={`${className}-button-expand`}
      >
        <Flag
          style={{ pointerEvents: 'none' }}
          countryCode={selectedLanguage}
          height={14 * size}
          className={`${className}-flag-selected`}
        />
        <FiChevronDown style={{ marginLeft: `${5 * size}px` }} />
      </MenuLinkItem>

      <Popper
        open={isOpen}
        anchorEl={refMenu.current}
        style={{ marginTop: `${-5 * size}px`, zIndex: 5000 }}
        transition={true}
        onMouseEnter={setShouldClose}
        popperOptions={{
          positionFixed: true,
        }}
        onMouseLeave={closePopper}
        modifiers={{
          arrow: {
            enabled: true,
          },
        }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <div>
              <div
                style={{
                  borderColor: 'transparent transparent white transparent',
                  borderStyle: 'solid',
                  borderWidth: `0 ${10 * size}px ${10 * size}px ${10 * size}px`,
                  height: '0',
                  marginBottom: '0px',
                  marginTop: `${-10 * size}px`,
                  pointerEvents: 'none',
                  position: 'relative',
                  width: '0',
                }}
                x-arrow=""
              />
              <Paper>
                <Flex
                  style={{
                    padding: `${0.5 * size}em ${0.25 * size}em`,
                    width: `${100 * size}px`,
                  }}
                  center={true}
                  wrap={true}
                >
                  {countryCodes.map((countryCode, index) => (
                    <Flag
                      className={`${className}-flag-${Number(index)}`}
                      key={countryCode}
                      countryCode={countryCode}
                      width={30 * size}
                      mb={10 * size}
                      mr={calculateMargin(
                        index,
                        index + 1 === countryCodes.length,
                      )}
                      onClick={changeLanguage(countryCode)}
                    />
                  ))}
                </Flex>
              </Paper>
            </div>
          </Fade>
        )}
      </Popper>
    </React.Fragment>
  )
}

export default LanguageSwitcher

export interface ILanguageSwitcherProps {
  selectedLanguage: CountryCode
}

export interface IClassName {
  className?: string
}

export interface ILanguageSwitcherDispatch {
  switchLanguage: (countryCode: CountryCode) => () => void
}
